/**
 * ClearPath Vendor idle timeout — the shared guard, wired to this app's auth.
 *
 * The timer, the warning modal and the precedence rules live in
 * @clearpath/design-system. What stays here is what only this app knows: its
 * default, its auth provider, and its own tenant-override query — codegen
 * requires a unique operation name per app, so the query cannot move.
 */
import type { ReactNode } from "react";
import { gql, type TypedDocumentNode } from "@apollo/client";

import { useQuery } from "@apollo/client/react";

import { IdleTimeoutGuard as SharedIdleTimeoutGuard } from '@clearpath/ui-components';

import { useAuth } from "../auth-provider";

const IDLE_APP_DEFAULT_MINUTES = 480;

const TENANT_IDLE_TIMEOUT: TypedDocumentNode<{ tenantSettings: { tenantId: string; idleTimeoutMinutes: number | null } | null }> = gql`
  query VendorTenantIdleTimeout {
    tenantSettings {
      tenantId
      idleTimeoutMinutes
    }
  }
`;

export function IdleTimeoutGuard({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth();
  const { data } = useQuery(TENANT_IDLE_TIMEOUT, {
    skip: !session,
    fetchPolicy: "cache-first",
    // A tenant without the settings read permission must not spam the log or
    // break the timer; fall back to the app default.
    errorPolicy: "all",
    context: { suppressForbiddenLog: true },
  });

  return (
    <SharedIdleTimeoutGuard
      defaultMinutes={IDLE_APP_DEFAULT_MINUTES}
      productLabel="ClearPath Vendor"
      enabled={Boolean(session)}
      tenantOverrideMinutes={data?.tenantSettings?.idleTimeoutMinutes ?? null}
      isJitGrant={Boolean(session?.isJitGrant)}
      onLogout={signOut}
    >
      {children}
    </SharedIdleTimeoutGuard>
  );
}
